import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feather, Link2, Eye, ShieldCheck } from 'lucide-react';
import { api, ApiError, type PackSummary, type SessionMode } from '@/lib/api';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';

const DEFAULT_PACK_SLUG = 'decouverte';

export function HomePage() {
  const navigate = useNavigate();
  const [pseudo, setPseudo] = useLocalStorage('ember.pseudo');
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState<'create' | 'join' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [packs, setPacks] = useState<PackSummary[]>([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [selectedPackSlug, setSelectedPackSlug] = useLocalStorage(
    'ember.lastPackSlug',
    DEFAULT_PACK_SLUG,
  );
  const [lastModeRaw, setLastModeRaw] = useLocalStorage('ember.lastMode', 'STANDARD');
  const guessModeEnabled = lastModeRaw === 'GUESS';
  const setGuessModeEnabled = (enabled: boolean) => setLastModeRaw(enabled ? 'GUESS' : 'STANDARD');

  // Charge la liste des packs disponibles au mount
  useEffect(() => {
    let cancelled = false;
    api
      .listPacks()
      .then((res) => {
        if (cancelled) return;
        setPacks(res.packs);
        if (!res.packs.some((p) => p.slug === selectedPackSlug)) {
          const fallback = res.packs.find((p) => p.slug === DEFAULT_PACK_SLUG) ?? res.packs[0];
          if (fallback) setSelectedPackSlug(fallback.slug);
        }
      })
      .catch(() => {
        // Silent fallback : si l'API packs est down, on cree avec le pack par defaut
      })
      .finally(() => {
        if (!cancelled) setPacksLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPack = useMemo(
    () => packs.find((p) => p.slug === selectedPackSlug),
    [packs, selectedPackSlug],
  );

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!pseudo.trim()) {
      setError("Choisis d'abord un pseudo");
      return;
    }
    setLoading('create');
    try {
      const mode: SessionMode = guessModeEnabled ? 'GUESS' : 'STANDARD';
      const res = await api.createSession(pseudo.trim(), selectedPackSlug || undefined, mode);
      navigate(`/s/${res.shortCode}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Erreur réseau');
    } finally {
      setLoading(null);
    }
  }

  function handleJoin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      setError('Le code fait 6 caractères');
      return;
    }
    navigate(`/s/${code}`);
  }

  return (
    <div className="max-w-2xl mx-auto space-y-12">
      {/* Hero : tagline + accent simultaneite + badge 18+ */}
      <section className="text-center space-y-4 pt-2">
        <div className="flex items-center justify-center gap-2">
          <h1 className="text-4xl sm:text-5xl font-bold text-primary tracking-tight">Ember</h1>
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border border-primary/40 text-primary">
            18+
          </span>
        </div>
        <p className="text-base sm:text-lg text-foreground/90 italic">
          Joue, explore, en toute liberté.
        </p>
        <p className="text-sm sm:text-base text-foreground/85 max-w-md mx-auto leading-relaxed">
          Vous répondez aux mêmes questions. Vos réponses se révèlent{' '}
          <span className="text-primary font-semibold">en même temps</span>. Pas avant, pas après.
        </p>
        <p className="text-xs sm:text-sm text-foreground/70 max-w-md mx-auto leading-relaxed">
          Pour relations sans étiquette : couples ouverts, crushes, amis proches, amants. Pas de
          case, pas de jugement, tu choisis ton intensité.
        </p>
      </section>

      {/* How it works : icones Lucide a la place des numeros, step 3 en emphasis */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StepCard
          icon={<Feather className="w-5 h-5" />}
          title="Crée ta partie"
          body="Choisis un pack et lance."
        />
        <StepCard
          icon={<Link2 className="w-5 h-5" />}
          title="Partage le lien"
          body="Le code à envoyer fait 6 lettres."
        />
        <StepCard
          icon={<Eye className="w-5 h-5" />}
          title="Révélation simultanée"
          body="10 questions. Vos réponses apparaissent au même moment."
          emphasis
        />
      </section>

      {/* Crée ta partie : bloc principal */}
      <section className="space-y-5">
        <h2 className="text-xl font-semibold text-center">Crée une partie</h2>

        <div className="space-y-2">
          <Label htmlFor="home-pseudo">Ton pseudo</Label>
          <Input
            id="home-pseudo"
            value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            placeholder="Ex: Alex"
            maxLength={30}
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label>Choisis ton pack</Label>
          {packsLoading ? (
            <PacksSkeleton />
          ) : packs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {packs.map((pack) => (
                <PackChoice
                  key={pack.slug}
                  pack={pack}
                  selected={pack.slug === selectedPackSlug}
                  onSelect={() => setSelectedPackSlug(pack.slug)}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              Liste indisponible, on utilisera le pack par défaut.
            </p>
          )}
          {selectedPack && (
            <p className="text-xs text-foreground/70 italic pt-1">{selectedPack.description}</p>
          )}
          <p className="text-xs text-foreground/70 pt-1">
            Tu choisiras ton intensité et tes sujets dans l'écran suivant.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-background/40 p-3">
          <label htmlFor="guess-mode" className="flex items-start gap-3 cursor-pointer select-none">
            <Checkbox
              id="guess-mode"
              checked={guessModeEnabled}
              onCheckedChange={(state) => setGuessModeEnabled(state === true)}
            />
            <span className="flex-1 space-y-1">
              <span className="block text-sm font-semibold">Mode Devine</span>
              <span className="block text-xs text-foreground/75 leading-relaxed">
                Pour chaque question, tu réponds ET tu devines la réponse de l'autre. Au reveal,
                vous voyez si vous vous connaissez vraiment.
              </span>
            </span>
          </label>
        </div>

        <form onSubmit={handleCreate} className="space-y-3">
          <Button
            type="submit"
            size="lg"
            className="w-full min-h-[48px] text-base"
            disabled={loading !== null}
          >
            {loading === 'create' ? 'Création...' : 'Créer ma partie'}
          </Button>
          <div className="flex items-start gap-2 text-xs text-foreground/70 justify-center text-center">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary" />
            <span>Pas de compte, pas de mot de passe. Ferme l'onglet, tout s'efface chez toi.</span>
          </div>
        </form>
      </section>

      {/* Rejoindre : bloc secondaire, plus compact */}
      <section className="border-t border-border pt-6">
        <form
          onSubmit={handleJoin}
          className="flex flex-col sm:flex-row sm:items-end gap-3"
          aria-label="Rejoindre une partie existante"
        >
          <div className="flex-1 space-y-1">
            <Label htmlFor="join-code" className="text-xs uppercase tracking-wider">
              Tu as un code ? Rejoins
            </Label>
            <Input
              id="join-code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="AB23CD"
              maxLength={6}
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              className="uppercase tracking-widest text-center font-mono text-lg"
            />
          </div>
          <Button
            type="submit"
            variant="secondary"
            className="min-h-[44px] sm:w-auto"
            disabled={loading !== null}
          >
            Rejoindre
          </Button>
        </form>
      </section>

      {error && (
        <p className="text-sm text-destructive text-center" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sous-composants
// ---------------------------------------------------------------------------

interface StepCardProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  /** Si true, on accentue la card (le step "differenciateur"). */
  emphasis?: boolean;
}

function StepCard({ icon, title, body, emphasis = false }: StepCardProps) {
  return (
    <Card
      className={
        emphasis
          ? 'border-primary/50 bg-primary/10 shadow-sm shadow-primary/10'
          : 'border-border bg-background/40'
      }
    >
      <CardContent className="pt-5 pb-5 space-y-2">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
              emphasis ? 'bg-primary/25 text-primary' : 'bg-primary/15 text-primary'
            }`}
            aria-hidden="true"
          >
            {icon}
          </span>
          <p className="font-semibold text-sm">{title}</p>
        </div>
        <p
          className={`text-xs leading-relaxed ${
            emphasis ? 'text-foreground/85' : 'text-foreground/70'
          }`}
        >
          {body}
        </p>
      </CardContent>
    </Card>
  );
}

interface PackChoiceProps {
  pack: PackSummary;
  selected: boolean;
  onSelect: () => void;
}

function PackChoice({ pack, selected, onSelect }: PackChoiceProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`text-left rounded-lg border p-3 transition-all min-h-[88px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        selected
          ? 'border-primary bg-primary/15 ring-2 ring-primary/40 shadow-sm shadow-primary/10'
          : 'border-border bg-background/40 hover:border-primary/50'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="font-semibold text-sm">{pack.name}</span>
        <span className="text-[10px] uppercase tracking-wider text-foreground/65">
          {pack.questionCount} Q
        </span>
      </div>
      <p className="text-xs text-foreground/70 leading-snug">{pack.description}</p>
    </button>
  );
}

function PacksSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="rounded-lg border border-border bg-background/40 p-3 min-h-[88px] animate-pulse"
        >
          <div className="h-4 w-2/3 bg-muted rounded mb-2" />
          <div className="h-3 w-full bg-muted rounded mb-1" />
          <div className="h-3 w-1/2 bg-muted rounded" />
        </div>
      ))}
    </div>
  );
}
